<template>
  <!-- Teleported into .admin-shell when there is one, so admin modals inherit
       gentelella's tokens and dark mode; otherwise to body, as before. -->
  <Teleport v-if="mounted"
    :to="target">
    <div class="modal-backdrop"
      :class="{ show: shown }"
      @click.self="$emit('close')">
      <div ref="dialog"
        :class="[$options.name, 'modal-dialog', { 'ak-modal--dark': dark }]"
        :style="{ maxWidth: width }"
        role="dialog"
        aria-modal="true"
        tabindex="-1">
        <button class="ak-modal__close"
          type="button"
          aria-label="Sulge"
          @click="$emit('close')">
          <i class="fa-solid fa-xmark" />
        </button>
        <div v-if="$slots.header"
          class="ak-modal__header">
          <slot name="header" />
        </div>
        <div class="ak-modal__body">
          <slot />
        </div>
        <div v-if="$slots.footer"
          class="ak-modal__footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
let modalId = 0

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const EXIT_MS = 220

export default {
  name: 'ak-modal',
  emits: ['close'],
  props: {
    open: { type: Boolean, default: false },
    width: { type: String, default: '560rem' },
    dark: { type: Boolean, default: false }
  },
  data() {
    return {
      pushedState: false,
      id: ++modalId,
      // `mounted` keeps the node alive through the exit transition; `shown`
      // drives the .show class the transition hangs off.
      mounted: false,
      shown: false,
      target: 'body',
      exitTimer: null,
      previousFocus: null,
    }
  },
  watch: {
    open(val) {
      if (val) this.onOpen()
      else this.onClose()
    }
  },
  mounted() {
    if (this.open) this.onOpen()
  },
  beforeUnmount() {
    this.teardown()
    clearTimeout(this.exitTimer)
    this.mounted = false
  },
  methods: {
    onOpen() {
      clearTimeout(this.exitTimer)
      // Resolved per open: the shell may not have existed when this component
      // was created, and a modal can outlive a route change.
      this.target = document.querySelector('.admin-shell') ? '.admin-shell' : 'body'
      this.previousFocus = document.activeElement
      this.mounted = true

      this.pushedState = true
      history.pushState({ modalId: this.id }, '')
      this.pushedUrl = location.href
      window.addEventListener('popstate', this.onPopState)
      document.addEventListener('keydown', this.onKeydown)
      this.lockScroll()

      // Two frames: one for the node to exist, one for the browser to register
      // the starting styles so the transition actually runs.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.shown = true
        this.$refs.dialog?.focus()
      }))
    },
    onClose() {
      this.shown = false
      this.teardown()
      this.exitTimer = setTimeout(() => { this.mounted = false }, EXIT_MS)

      const back = this.previousFocus
      this.previousFocus = null
      if (back && typeof back.focus === 'function') back.focus()
    },
    teardown() {
      window.removeEventListener('popstate', this.onPopState)
      document.removeEventListener('keydown', this.onKeydown)
      if (this.pushedState) {
        this.pushedState = false
        // Only while its own entry is current: going back after a navigation from inside the modal would undo it.
        // The URL tells, since the router's replace copies the modal's state into its own entry.
        if (history.state?.modalId === this.id && location.href === this.pushedUrl) history.back()
      }
      this.unlockScroll()
    },
    lockScroll() {
      if (window.innerWidth <= 640) {
        document.documentElement.style.overflow = 'hidden'
      }
    },
    unlockScroll() {
      document.documentElement.style.overflow = ''
    },
    onKeydown(e) {
      if (!this.open) return
      if (e.key === 'Escape') {
        this.$emit('close')
        return
      }
      // ElDialog trapped Tab; keep that rather than let focus wander behind
      // the backdrop.
      if (e.key !== 'Tab') return
      const dialog = this.$refs.dialog
      if (!dialog) return
      const items = [...dialog.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null)
      if (!items.length) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
    onPopState(e) {
      if (this.open && (!e.state || e.state.modalId !== this.id)) {
        this.pushedState = false
        this.$emit('close')
      }
    }
  }
}
</script>

<style lang="scss">
// Sits on gentelella's .modal-dialog and overrides it back to the roomier
// dialog the public site has always used. admin-theme.scss hands the admin
// back to gentelella's compact sizing.
.ak-modal {
  position: relative;
  border-radius: 20rem;
  overflow-y: auto;
  background: #fff;
  color: var(--text-color);

  // ElDialog wrapped every slot in .el-dialog__body and padded that, so the
  // padding has to sit on the dialog itself — not on .ak-modal__body, which
  // would leave the header, footer and close button flush to the edge.
  padding: 56rem 48rem 44rem;

  // gentelella's .modal-dialog draws a 1px border plus a 1px ring in its
  // box-shadow. Neither existed on the Element Plus dialog.
  border: none;
  box-shadow: 0 12rem 32rem rgba(0, 0, 0, 0.08), 0 8rem 20rem rgba(0, 0, 0, 0.06);

  .ak-modal__body {
    color: inherit;
  }

  @include max-screen(640) {
    max-width: none !important;
    max-height: none;
    width: 100%;
    height: 100%;
    border-radius: 0;
    padding: 32rem 20rem 24rem;
    display: flex;
    flex-direction: column;

    .ak-modal__body {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }
}

// Full-bleed on phones: gentelella pads the backdrop to float the dialog.
@include max-screen(640) {
  .modal-backdrop:has(.ak-modal) {
    padding: 0;
    overflow: hidden;
  }
}

.ak-modal__header {
  margin-bottom: 24rem;
}

.ak-modal__footer {
  margin-top: 24rem;
  text-align: center;
}

// Shared modal content classes
.modal-title {
  font-size: 24rem;
  font-weight: 700;
  color: var(--primary);
  text-align: center;
  margin-bottom: 12rem;
}

.modal-desc {
  text-align: center;
  color: #666;
  font-size: 15rem;
  line-height: 1.6;
  margin-bottom: 24rem;
}

.modal-icon {
  width: 44rem;
  height: 44rem;
  border-radius: 12rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18rem;
  background: rgba(0, 174, 239, 0.1);
  color: var(--accent);
  flex-shrink: 0;
}

.ak-modal--dark {
  background: #1a2a32;
  color: #dce4e8;

  // Close button
  .ak-modal__close {
    background: rgba(255, 255, 255, 0.08);
    color: #8a9aa4;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
    }
  }

  // Reusable dark typography
  h2,
  .dark-title {
    color: #fff;
  }

  p,
  .dark-muted {
    color: #8a9aa4;
  }

  strong {
    color: #c0cdd4;
  }

  a {
    color: #8a9aa4;

    &:hover {
      color: var(--accent);
    }
  }

  // Icons
  .modal-icon {
    background: rgba(0, 174, 239, 0.15);
    color: var(--accent);
  }

  // Pricing
  .pricing-label {
    color: #c0cdd4;
  }

  .pricing-dots {
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .pricing-price {
    color: var(--accent);
  }

  .pricing-row {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .pricing-row--sub {
    .pricing-label {
      color: #7a8a94;
    }

    .pricing-price {
      color: rgba(0, 174, 239, 0.8);
    }
  }

  // Fields read these tokens, so on the dark dialog they turn dark with it.
  .field {
    --bg-surface: rgba(255, 255, 255, 0.06);
    --bg-surface-secondary: rgba(255, 255, 255, 0.03);
    --border-translucent: rgba(255, 255, 255, 0.14);
    --text: #dce4e8;
    --text-secondary: #a9b6bd;
    --text-disabled: #6a7a84;
    // --bg-surface above laid over the dialog's #1a2a32.
    --field-fill: #28373e;
  }

  .field__control {
    color-scheme: dark;
  }

  // Buttons
  .btn-primary {
    background: var(--accent);
    color: #fff;

    &:hover {
      background: var(--accent-hover);
    }
  }
}

.ak-modal__close {
  position: absolute;
  top: 16rem;
  right: 16rem;
  width: 36rem;
  height: 36rem;
  border: none;
  border-radius: 50%;
  background: var(--section-alt-bg, #f4f7f9);
  color: #666;
  font-size: 18rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 1;

  &:hover {
    background: #e0e0e0;
    color: var(--primary);
  }
}
</style>
